import React, { useCallback, useEffect, useRef, useState, } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { DefaultTheme } from '../../Themes';
import SelectedOptionDisplayer from './SelectedOptionDisplayer';
import OptionMenu from './OptionMenu';
const StyledContainer = styled.div `
  position: relative;
  display: inline-flex;
  min-width: 7rem;
  padding: 0.666666667rem 1rem;
  background-color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
  border: 2px solid #d7dae0;
  border-radius: 4px;
  box-sizing: border-box;
  outline: 0;
  cursor: pointer;
  user-select: none;
`;
const StyledMenuContainer = styled.div `
  padding: 0.666666667rem 0;
  background-color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
  border-radius: 4px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.11), 0 1px 3px rgba(0, 0, 0, 0.16);
  margin-top: ${({ menuGap }) => menuGap}px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1;
`;
const StyledClearableContainer = styled.div `
  text-align: center;
`;
const StyledClearableLabel = styled.span `
  font-weight: 700;
  cursor: pointer;
`;
const Select = ({ className, displayerClassName, displayerIconClassName, menuContainerClassName, menuClassName, optionWrapperClassName, selectedOptionClassName, defaultOptions = [], options, onClick = () => { }, onSelect = () => { }, onBlur = () => { }, isMultiSelect, isClearable, clearLabel = 'Clear', placeholder = 'Select', allSelectedLabel, optionComponent, menuGap = 3, selectedOptionSeparator = ', ', theme = DefaultTheme, }) => {
    const [selectedOptions, setSelectedOptions] = useState(defaultOptions);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuWasClicked, setMenuWasClicked] = useState(false);
    const containerRef = useRef(null);
    // The use of `JSON.stringify` is because the `useEffect` hook does not support deep equality checks.
    useEffect(() => {
        setSelectedOptions(defaultOptions);
    }, [JSON.stringify(defaultOptions)]);
    const handleContainerClick = (event) => {
        onClick(event);
        setIsMenuOpen(!isMenuOpen);
    };
    const handleMenuMouseDown = useCallback(() => {
        setMenuWasClicked(true);
    }, [menuWasClicked]);
    const handleContainerBlur = useCallback((event) => {
        if (menuWasClicked) {
            // If the menu was clicked, we keep the focus on the container because the menu is a child of it.
            event.currentTarget.focus();
            setMenuWasClicked(false);
        }
        else {
            setIsMenuOpen(false);
            onBlur();
        }
    }, [menuWasClicked, onBlur]);
    const handleOptionSelection = useCallback((selectedOption) => {
        if (isMultiSelect) {
            const filteredOptions = selectedOptions.filter(({ name }) => name !== selectedOption.name);
            let newSelectedOptions;
            if (selectedOptions.length === filteredOptions.length) {
                // If the length of both arrays is the same, it means that the selected option was not
                // selected before, so we add it.
                newSelectedOptions = filteredOptions.concat(selectedOption);
            }
            else {
                // If not, we already filtered the option the user deselected.
                newSelectedOptions = filteredOptions;
            }
            setSelectedOptions(newSelectedOptions);
            onSelect(newSelectedOptions);
        }
        else {
            setSelectedOptions([selectedOption]);
            setIsMenuOpen(false);
            onSelect(selectedOption);
        }
    }, [selectedOptions, onSelect, isMultiSelect]);
    const handleClearOptions = useCallback(() => {
        setSelectedOptions([]);
    }, []);
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement(StyledContainer, { className: className, ref: containerRef, tabIndex: 0, onClick: handleContainerClick, onBlur: handleContainerBlur },
            React.createElement(SelectedOptionDisplayer, { className: displayerClassName, iconClassName: displayerIconClassName, placeholder: placeholder, allSelected: selectedOptions.length === options.length, allSelectedLabel: allSelectedLabel, selectedOptions: selectedOptions, selectedOptionSeparator: selectedOptionSeparator }),
            isMenuOpen && (React.createElement(StyledMenuContainer, { className: menuContainerClassName, onMouseDown: handleMenuMouseDown, menuGap: menuGap },
                React.createElement(OptionMenu, { className: menuClassName, optionWrapperClassName: optionWrapperClassName, selectedOptionClassName: selectedOptionClassName, options: options, selectedOptions: selectedOptions, onOptionSelect: handleOptionSelection, optionComponent: optionComponent, isClearable: isClearable }),
                isClearable && (React.createElement(StyledClearableContainer, { onClick: handleClearOptions },
                    React.createElement(StyledClearableLabel, null, clearLabel))))))));
};
export default Select;
